import AppContext from "@common/contexts/App";
import Button from "@common/controls/Button/component";
import Countdown from "@common/controls/Countdown/component";
import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormField } from "@common/controls/Form/component";
import { RadioButtonGroup } from "@common/controls/Inputs/Radio";
import { TextInput } from "@common/controls/Inputs/Text";
import ConfirmModal from "@common/controls/Modal/Modals/Confirm/component";
import { FormModalControlled, type FormModalControlledRenderParams } from "@common/controls/Modal/Modals/Form/component";
import { type OptionItem, Users } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { useVisibleModal } from "@common/lib/hooks";
import { EMPTY_OBJECT } from "@common/lib/misc";
import { isUndefined } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { getLocalization } from "./lib";

import "./style.less";

type FormData = {
  code: string;
  destination: string;
  method?: OtpStepMethod;
  stepId: string;
};
type OtpStepsModalProps = Omit<Props, "email" | "phone"> & {
  steps: OtpStep[];
};
export const OtpStepsModal = (props: OtpStepsModalProps) => {
  const i18n = useI18n();

  const [selectedStepId, setSelectedStepId] = useState<string>();
  const [codeSentStepId, setCodeSentStepId] = useState<string>();
  const [expiryCodeTime, setExpiryCodeTime] = useState<Date>();
  const [isCodeValid, setIsCodeValid] = useState<boolean>();
  const [method, setMethod] = useState<OtpStepMethod>();
  const [isResendingCode, setIsResendingCode] = useState<boolean>();

  const appContext = React.useContext(AppContext);

  const steps = useMemo(() => {
    const step = props.steps.find((x) => x.id === codeSentStepId);
    return step ? [step] : props.steps;
  }, [props.steps, codeSentStepId]);

  const onGetCodeClick = React.useCallback(async () => {
    setIsResendingCode(true);
    try {
      const expiryTime = await props.sendCode(props.steps.find((x) => x.id === selectedStepId)!, method);
      setExpiryCodeTime(typeof expiryTime === "string" ? new Date(expiryTime) : expiryTime);
      setCodeSentStepId(selectedStepId);
    } finally {
      setIsResendingCode(false);
    }
  }, [selectedStepId, props.steps, props.sendCode, method]);

  const onSubmit = React.useCallback(
    async (data: FormData) => {
      if (codeSentStepId) {
        const isValid = await props.validateCode(props.steps.find((x) => x.id === selectedStepId)!, data.code);
        setIsCodeValid(isValid);
      } else {
        await onGetCodeClick();
      }
    },
    [props.steps, selectedStepId, codeSentStepId, props.validateCode, onGetCodeClick]
  );

  const onChange = React.useCallback((name: keyof FormData, value: any) => {
    switch (name) {
      case "stepId": {
        setSelectedStepId(value);
        break;
      }
      case "method": {
        setMethod(value);
        break;
      }
    }
  }, []);

  useEffect(() => setSelectedStepId(steps[0]?.id), [steps]);

  const topInfo = React.useMemo(() => {
    if (!steps.length) return;
    if (steps.length > 1) return i18n.infoOTPSendOption;

    const step = steps[0];
    return (
      step.message ??
      getLocalization(i18n, codeSentStepId ? "sentInfo" : "sendInfo", step.messageType, step.destination)
    );
  }, [i18n, codeSentStepId, steps]);

  const footerBottomInfo = useMemo(() => {
    if (!codeSentStepId) return;

    return (
      <div className="send_code_container">
        {expiryCodeTime && expiryCodeTime.getTime() > Date.now() ? (
          <>
            {i18n.infoOTPExpiresIn}
            <Countdown date={expiryCodeTime} onComplete={() => setExpiryCodeTime(undefined)} />
          </>
        ) : (
          <>
            <Button type="link" className="resend_btn" isDisabled={isResendingCode} onClick={onGetCodeClick}>
              {i18n.resend}
            </Button>
          </>
        )}
      </div>
    );
  }, [onGetCodeClick, codeSentStepId, expiryCodeTime]);

  const otpOptions = React.useMemo<OptionItem<any>[]>(
    () =>
      steps.map((x) => ({
        label: getLocalization(i18n, "optionLabel", x.messageType, x.destination),
        value: x.id
      })),
    [i18n, steps]
  );

  return (
    <FormModalControlled
      isLoading={props.isLoading}
      isVisible={props.isVisible}
      title={i18n.verification}
      defaultValues={EMPTY_OBJECT}
      className="otp_modal"
      width={appContext.context === "admin" ? 500 : 450}
      onClose={props.onClose}
      submit={onSubmit}
      onChange={onChange}
    >
      {({ values }: FormModalControlledRenderParams<FormData>) => (
        <>
          {topInfo}
          {steps.length > 1 ? (
            <FormField
              name="stepId"
              value={selectedStepId}
              Component={RadioButtonGroup}
              componentProps={{
                orientation: "vertical",
                options: otpOptions,
                showLabel: true
              }}
            />
          ) : (
            !!steps[0].methods && (
              <FormField
                isRequired
                name="method"
                value={values.method}
                Component={RadioButtonGroup}
                componentProps={{
                  orientation: "vertical",
                  options: steps[0].methods.map((x) => ({
                    label: i18n[x],
                    value: x
                  })),
                  showLabel: true
                }}
              />
            )
          )}
          {!!codeSentStepId && (
            <FormField
              name="code"
              value={values.code}
              isRequired
              errorMessage={isCodeValid === false ? i18n.invalidOneTimePassword : null}
              Component={TextInput}
              label={i18n.otp}
              componentProps={{
                maxLength: 8
              }}
            />
          )}
          {appContext.context === "admin" ? (
            <FormButtons>
              {footerBottomInfo}
              <CancelButton />
              <SubmitButton>{codeSentStepId ? i18n.confirm : i18n.requestOTP}</SubmitButton>
            </FormButtons>
          ) : (
            <FormButtons align="justify">
              <SubmitButton>{codeSentStepId ? i18n.confirm : i18n.requestOTP}</SubmitButton>
              {footerBottomInfo}
            </FormButtons>
          )}
        </>
      )}
    </FormModalControlled>
  );
};

export type OtpStep = Users.Api.OtpStep;
export type OtpStepMethod = Users.Api.OtpStepMethod;
export type MessageType = Users.Api.OtpMessageType;

export type OtpModalMapProps = {
  email: string;
  isLoading?: boolean;
  phone: string | null;
  isVisible: boolean;
};
export type OtpModalActionProps = {
  onClose?: () => void;
  validateCode: (step: OtpStep, otp: string) => Promise<boolean>;
  sendCode: (step: OtpStep, method: OtpStepMethod | undefined) => Promise<Date | string>;
};

type Props = OtpModalActionProps & OtpModalMapProps;
const OtpModal = ({ email, phone, ...props }: Props) => {
  const steps = useMemo<OtpStep[]>(() => {
    const result: OtpStep[] = [
      {
        destination: email,
        id: "Email",
        messageType: "Email"
      }
    ];

    if (phone) {
      result.push({
        destination: phone,
        id: "Text",
        messageType: "Text"
      });
    }

    return result;
  }, [email, phone]);

  return <OtpStepsModal {...props} steps={steps} />;
};

export type ConfirmData = {
  cancelCaption?: string;
  confirmCaption?: string;
  message?: string;
  onConfirm: () => void;
  title?: string;
};
export type OtpSendLinkModalProps = {
  confirmData?: ConfirmData;
  isLoading?: boolean;
  isVisible: boolean;
  onClose?: () => void;
  onVerified: (isSuccess: boolean) => void;
  userName: string;
  sendLink: (step: OtpStep) => Promise<Date>;
  steps: OtpStep[];
  verify: () => Promise<boolean | null>;
};
export type OtpSendLinkFormData = {
  stepId: string;
};
export const OtpSendLinkModal = (props: OtpSendLinkModalProps) => {
  const i18n = useI18n();

  const [expiryCodeTime, setExpiryCodeTime] = useState<Date>();
  const [isLinkSent, setIsLinkSent] = useState<boolean>(false);
  const [visibleModal, setVisibleModal, hideVisibleModal] = useVisibleModal<"confirm">();

  React.useEffect(() => {
    if (!isLinkSent || isUndefined(expiryCodeTime)) return;

    let timeout;
    let isCancelled = false;
    const verify = async () => {
      const res = await props.verify();
      if (isCancelled) return;

      if (res !== null) {
        props.onVerified(res);
        return;
      }

      timeout = setTimeout(verify, 5000);
    };

    verify();

    return () => {
      isCancelled = true;
      clearTimeout(timeout);
    };
  }, [isLinkSent, props.verify, props.onVerified, expiryCodeTime]);

  const onSubmit = React.useCallback(
    async (data: OtpSendLinkFormData) => {
      const expiryTime = await props.sendLink(props.steps.find((x) => x.id === data.stepId)!);
      setExpiryCodeTime(expiryTime);
      setIsLinkSent(true);
    },
    [props.sendLink, props.steps]
  );

  const defaultValues = React.useMemo(
    (): OtpSendLinkFormData => ({
      stepId: props.steps[0]?.id
    }),
    [props.steps]
  );

  const topInfo = React.useCallback(
    (stepId: string) => {
      if (!props.steps.length || !stepId) return null;

      if (props.steps.length > 1 && !isLinkSent) {
        return i18n.infoOTLSendOption;
      }

      const step = props.steps.find((x) => x.id === stepId)!;
      return getLocalization(
        i18n,
        isLinkSent ? "sentInfo" : "sendInfo",
        step.messageType,
        step.destination,
        props.userName,
        true
      );
    },
    [i18n, isLinkSent, props.steps]
  );

  const footerBottomInfo = useMemo(() => {
    if (!isLinkSent) return;

    return (
      <div className="send_code_container">
        {expiryCodeTime && new Date(expiryCodeTime).getTime() > Date.now() ? (
          <>
            {i18n.infoOTLExpiresIn}
            <Countdown
              date={expiryCodeTime}
              onComplete={() => {
                setExpiryCodeTime(undefined);
              }}
            />
          </>
        ) : (
          <Button
            type="link"
            className="resend_btn"
            onClick={() => {
              if (props.steps.length === 1) return onSubmit({ stepId: props.steps[0].id });
              setIsLinkSent(false);
            }}
          >
            {i18n.resend}
          </Button>
        )}
      </div>
    );
  }, [i18n, expiryCodeTime, isLinkSent, props.steps]);

  const otpOptions = React.useMemo<OptionItem<any>[]>(
    () =>
      props.steps.map((x) => ({
        label: getLocalization(i18n, "optionLabel", x.messageType, x.destination),
        value: x.id
      })),
    [i18n, props.steps]
  );

  return (
    <>
      <FormModalControlled
        isLoading={props.isLoading}
        className="otp_modal"
        defaultValues={defaultValues}
        isVisible={props.isVisible}
        onClose={() => {
          if (isLinkSent) {
            setVisibleModal("confirm");
            return;
          }

          props.onClose && props.onClose();
        }}
        submit={onSubmit}
        title={i18n.authorization}
        width="400px"
      >
        {({ values }: FormModalControlledRenderParams<OtpSendLinkFormData>) => (
          <>
            {topInfo(values.stepId)}
            {props.steps.length > 1 && !isLinkSent && (
              <FormField
                name="stepId"
                value={values.stepId}
                Component={RadioButtonGroup}
                componentProps={{
                  orientation: "vertical",
                  options: otpOptions,
                  showLabel: true
                }}
              />
            )}

            <FormButtons>
              {footerBottomInfo}
              {!isLinkSent && <SubmitButton>{i18n.send}</SubmitButton>}
            </FormButtons>
          </>
        )}
      </FormModalControlled>
      {props.confirmData && (
        <ConfirmModal
          cancelCaption={props.confirmData.cancelCaption}
          isVisible={visibleModal === "confirm"}
          onClose={hideVisibleModal}
          type={{
            label: props.confirmData.confirmCaption,
            confirmType: "cancel",
            type: "danger"
          }}
          message={props.confirmData.message}
          title={props.confirmData.title}
          onConfirm={() => {
            props.confirmData!.onConfirm();
            props.onClose && props.onClose();
            hideVisibleModal();
          }}
        />
      )}
    </>
  );
};
export default OtpModal;
